import {
  checkoutNext,
  PUBLISHED_PLAN_PRICES,
  type Account,
  type BillingInterval,
  type CheckoutChoice,
  type PaidPlanId,
  type Plan,
  type PlanChangePreview,
  type PlanId,
} from '@lyrmo/shared';
import { useId } from 'react';
import { Button } from '../../components/Button';
import { RollingNumber } from '../../components/RollingNumber';
import { useI18n, type I18n } from '../../i18n/i18n-context';
import { rich } from '../../i18n/rich';
import { cx } from '../../lib/cx';
import { useAccount } from './account-context';
import { CreditRatesTable } from './CreditRatesTable';
import { SignInForm } from './SignInForm';
import type { PlanChooserState } from './usePlanChooser';

// Choosing a plan, in the plans dialog and on the pricing page alike: the plans, monthly or
// yearly, then signing in first, checkout, or what a switch costs (usePlanChooser keeps where it
// has got to).

/** Renewal or payment status for the plan the viewer pays for. */
function subscriptionNote(
  { plan, subscription }: Account,
  { t, format }: I18n,
): string | undefined {
  // A lapsed subscription doesn't describe the current plan.
  if (subscription?.plan !== plan) return undefined;
  if (subscription.status === 'past_due') return t.plans.pastDue;
  if (!subscription.currentPeriodEnd) return undefined;
  const date = format.date(subscription.currentPeriodEnd);
  return subscription.cancelAtPeriodEnd ? t.plans.endsOn(date) : t.plans.renewsOn(date);
}

/** Use Paddle's price when available and the published price while its catalog is unavailable. */
function displayedPrice(plan: Plan, interval: BillingInterval) {
  return (
    plan.prices[interval] ?? (plan.id === 'free' ? null : PUBLISHED_PLAN_PRICES[plan.id][interval])
  );
}

/** Whole months saved by paying yearly on every paid plan, such as 2 for ten months' price. */
function monthsFreeYearly(plans: readonly Plan[]): number {
  const saved = plans
    .filter((plan) => plan.id !== 'free')
    .map((plan) => {
      const month = displayedPrice(plan, 'month');
      const year = displayedPrice(plan, 'year');
      if (!month || !year || month.amount === 0 || month.currency !== year.currency) return 0;
      return Math.floor(12 - year.amount / month.amount);
    });
  return saved.length > 0 ? Math.max(0, Math.min(...saved)) : 0;
}

interface IntervalToggleProps {
  readonly interval: BillingInterval;
  readonly monthsFree: number;
  readonly onChange: (interval: BillingInterval) => void;
}

function IntervalToggle({ interval, monthsFree, onChange }: IntervalToggleProps) {
  const { t } = useI18n();
  return (
    <div
      className="interval-toggle"
      data-interval={interval}
      role="group"
      aria-label={t.plans.billingPeriod}
    >
      <Button
        aria-pressed={interval === 'month'}
        onClick={() => {
          onChange('month');
        }}
      >
        {t.plans.monthly}
      </Button>
      <Button
        aria-pressed={interval === 'year'}
        onClick={() => {
          onChange('year');
        }}
      >
        {t.plans.yearly}
        {monthsFree > 0 && (
          <>
            {' '}
            <span className="interval-saving">{t.plans.monthsFree(monthsFree)}</span>
          </>
        )}
      </Button>
    </div>
  );
}

function PlansSkeleton() {
  const { t } = useI18n();
  return (
    <div role="status" aria-label={t.plans.loading} className="plans-skeleton">
      <span className="sr-only">{t.plans.loading}</span>
      <div className="interval-toggle plan-skeleton-toggle" aria-hidden="true">
        <span className="skeleton-line" />
        <span className="skeleton-line" />
      </div>
      <div className="plan-grid" aria-hidden="true">
        {[0, 1, 2, 3].map((card) => (
          <div className="plan-card plan-card--skeleton" key={card}>
            <span className="skeleton-line plan-skeleton-name" />
            <span className="skeleton-line plan-skeleton-price" />
            <span className="skeleton-line plan-skeleton-feature" />
            <span className="skeleton-line plan-skeleton-action" />
          </div>
        ))}
      </div>
    </div>
  );
}

interface PlanCardProps {
  readonly plan: Plan;
  readonly interval: BillingInterval;
  readonly current: PlanId;
  /** How often the current plan is billed, when that's known. */
  readonly currentInterval: BillingInterval | null;
  /** Shown under the current plan. */
  readonly note: string | undefined;
  readonly busy: boolean;
  readonly planChangesEnabled: boolean;
  readonly onChoose: (plan: PaidPlanId) => void;
}

function PlanCard({
  plan,
  interval,
  current,
  currentInterval,
  note,
  busy,
  planChangesEnabled,
  onChoose,
}: PlanCardProps) {
  const { t, format } = useI18n();
  const name = t.planNames[plan.id];
  const isCurrentPlan = plan.id === current;
  // The current plan billed the other way is one to switch to.
  const isCurrent = isCurrentPlan && (currentInterval === null || currentInterval === interval);
  const subscribed = current !== 'free';
  const titleId = `plan-${plan.id}`;
  const tooltipId = useId();
  const price = displayedPrice(plan, interval);
  // Yearly, what twelve months billed monthly would come to, crossed out over the yearly price.
  const monthly = displayedPrice(plan, 'month');
  const regular =
    interval === 'year' &&
    price &&
    monthly?.currency === price.currency &&
    monthly.amount * 12 > price.amount
      ? format.price(monthly.amount * 12, price.currency)
      : null;

  let action;
  if (isCurrent) {
    action = <Button disabled>{t.plans.current}</Button>;
  } else if (plan.id === 'free') {
    action = subscribed && <p className="plan-note">{t.plans.cancelAnytime}</p>;
  } else {
    const paidPlan = plan.id;
    const actionLabel = isCurrentPlan
      ? interval === 'year'
        ? t.plans.switchToYearly
        : t.plans.switchToMonthly
      : subscribed
        ? t.plans.switchTo(name)
        : t.plans.choose(name);
    const button = (
      <Button
        variant="ghost"
        disabled={busy || !planChangesEnabled}
        aria-describedby={planChangesEnabled ? undefined : tooltipId}
        onClick={() => {
          onChoose(paidPlan);
        }}
      >
        {actionLabel}
      </Button>
    );
    action = planChangesEnabled ? (
      button
    ) : (
      <span
        className="plan-action-tooltip-anchor"
        role="group"
        aria-label={actionLabel}
        aria-describedby={tooltipId}
        tabIndex={0}
      >
        {button}
        <span className="plan-action-tooltip" id={tooltipId} role="tooltip">
          {t.plans.actionsUnavailable}
        </span>
      </span>
    );
  }

  return (
    <article className={cx('plan-card', isCurrent && 'is-current')} aria-labelledby={titleId}>
      <header className="plan-card-header">
        <h3 id={titleId}>{name}</h3>
      </header>
      {/* On every card, empty where there's no saving, so the prices stay in line. */}
      {interval === 'year' && (
        <p className="plan-price-regular">
          {regular && (
            <>
              <s aria-hidden="true">{regular}</s>
              <span className="sr-only">{t.plans.billedMonthly(regular)}</span>
            </>
          )}
        </p>
      )}
      <p className="plan-price">
        {price ? (
          <>
            <strong>
              <RollingNumber value={format.price(price.amount, price.currency)} />
            </strong>
            <span> / {t.plans.per[price.interval]}</span>
          </>
        ) : (
          <strong>{t.plans.free}</strong>
        )}
      </p>
      <ul className="plan-features">
        <li>
          {rich(t.plans.creditsADay(plan.creditsPerDay), {
            count: <strong>{format.number(plan.creditsPerDay)}</strong>,
          })}
        </li>
      </ul>
      {action}
      {isCurrent && note && <p className="plan-note">{note}</p>}
    </article>
  );
}

interface PlanChangeProps {
  readonly choice: CheckoutChoice;
  /** Null while it's on its way. */
  readonly preview: PlanChangePreview | null;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onConfirm: () => void;
  readonly onBack: () => void;
}

/** What switching plans costs now, from the payment provider, with a button to go ahead. */
function PlanChange({ choice, preview, busy, error, onConfirm, onBack }: PlanChangeProps) {
  const { t, format } = useI18n();
  let cost;
  if (!preview) cost = t.plans.previewing;
  else if (preview.amount === 0) cost = t.plans.noChargeNow;
  else {
    const amount = format.price(preview.amount, preview.currency);
    cost = preview.action === 'credit' ? t.plans.creditNow(amount) : t.plans.chargeNow(amount);
  }
  return (
    <div className="plan-change">
      <p>
        <strong>{t.plans.changeTo(t.planNames[choice.plan], choice.interval === 'year')}</strong>
      </p>
      <p role="status">{cost}</p>
      <p className="plan-note">{t.plans.changeNote}</p>
      {error && (
        <p className="dialog-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <Button disabled={busy} onClick={onBack}>
          {t.common.back}
        </Button>
        <Button variant="primary" disabled={busy || !preview} onClick={onConfirm}>
          {busy ? t.plans.changing : t.plans.confirmChange}
        </Button>
      </div>
    </div>
  );
}

interface PlanChooserProps {
  readonly chooser: PlanChooserState;
  /** After a change of plan goes through. */
  readonly onChanged: () => void;
}

/** The plans to choose from, or the step after choosing one. */
export function PlanChooser({ chooser, onChanged }: PlanChooserProps) {
  const i18n = useI18n();
  const { t } = i18n;
  const { account, plans, planChangesEnabled, plansError, openDialog, signOut } = useAccount();
  const { interval, current, currentInterval, signingInFor, change, busy, error, user } = chooser;

  if (signingInFor) {
    return (
      <SignInForm
        intro={t.plans.signInIntro(t.planNames[signingInFor], interval === 'year')}
        next={checkoutNext({ plan: signingInFor, interval })}
        onBack={() => {
          chooser.setSignInFor(null);
        }}
      />
    );
  }
  if (change) {
    return (
      <PlanChange
        choice={change.choice}
        preview={change.preview}
        busy={busy}
        error={change.previewError ?? error}
        onConfirm={() => {
          chooser.confirmChange(change.choice, onChanged);
        }}
        onBack={() => {
          chooser.setChange(null);
          chooser.setError(null);
        }}
      />
    );
  }
  return (
    <>
      {plans ? (
        <>
          <IntervalToggle
            interval={interval}
            monthsFree={monthsFreeYearly(plans)}
            onChange={chooser.setBillingInterval}
          />
          <div className="plan-grid">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                interval={interval}
                current={current}
                currentInterval={currentInterval}
                note={account ? subscriptionNote(account, i18n) : undefined}
                busy={busy}
                planChangesEnabled={planChangesEnabled}
                onChoose={chooser.choose}
              />
            ))}
          </div>
        </>
      ) : plansError ? (
        <p className="dialog-error" role="alert">
          {plansError}
        </p>
      ) : (
        <PlansSkeleton />
      )}
      <CreditRatesTable />
      {error && (
        <p className="dialog-error" role="alert">
          {error}
        </p>
      )}
      <footer className="plans-footer">
        <div className="plans-account">
          {user ? (
            <>
              <span className="plans-account-status">
                {rich(t.common.signedInAs, { email: <strong>{user.email}</strong> })}
              </span>
              <span className="plans-account-actions">
                {current !== 'free' && (
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={() => {
                      openDialog('billing');
                    }}
                  >
                    {t.common.manageBilling}
                  </button>
                )}
                <button
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => void chooser.run(signOut)}
                >
                  {t.common.signOut}
                </button>
              </span>
            </>
          ) : (
            <span className="plans-account-status">
              {t.plans.alreadySubscribed}{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  chooser.setError(null);
                  openDialog('sign-in');
                }}
              >
                {t.common.signIn}
              </button>
            </span>
          )}
        </div>
        <p className="dialog-footnote">{t.plans.footnote}</p>
      </footer>
    </>
  );
}
